import { useLazyQuery } from "@apollo/client/react";
import { useRef, useState } from "react";
import { VALIDAR_DISCREPANCIA_VISITA } from "../../../data/graphQL/queries/MisVisitas/validaciones/validarDiscrepanciaHorariaVisita";
import { MensajeAdvertenciaDialog } from "../components/components/Dialogs/MensajeAdvertenciaDialog";

/**
 * Hook personalizado para validar la discrepancia de fechas de una visita
 * Maneja la lógica de validación, modal y promesas pendientes
 * 
 * @returns {Object} Objeto con:
 *   - validarDiscrepanciaHoraria: función async que valida la discrepancia de fechas
 *   - modal: componente JSX del modal de advertencia
 * 
 * @example
 * const { validarDiscrepanciaHoraria, modal } = useValidacionDiscrepanciaHorariaVisita();
 * 
 * const handleFinalizar = async () => {
 *   const resultado = await validarDiscrepanciaHoraria(idVisita);
 *   if (resultado.permitir) {
 *     // Continuar con el flujo
 *   }
 * };
 */
export function useValidacionDiscrepanciaHorariaVisita() {
    const [modalMensajeDiscrepanciaHorariaVisita, setModalMensajeDiscrepanciaHoraVisita] = useState(false);
    const [errorDiscrepanciaHoraria, setErrorDiscrepanciaHoraria] = useState(null);
    const pendingRef = useRef({});

    const [validarDiscrepanciaHoraria] = useLazyQuery(VALIDAR_DISCREPANCIA_VISITA, {
        fetchPolicy: 'no-cache'
    });

    /**
     * Valida la discrepancia de fechas de la visita
     * Si hay error, muestra el modal y retorna una promesa que se resuelve 
     * cuando el usuario hace click en aceptar o cancelar
     * 
     * @param {number} idVisita - ID de la visita a validar
     * @param {string} fechaInicioUsuario - Fecha de inicio ingresada por el usuario
     * @param {string} fechaFinUsuario - Fecha de fin ingresada por el usuario
     * @returns {Promise<Object>} Promesa que resuelve con { permitir: boolean }
     */
    const validarDiscrepancia = async (idVisita, fechaInicioUsuario, fechaFinUsuario) => {
        try {
            const { data: dataValidarDiscrepanciaHoraria } = await validarDiscrepanciaHoraria({
                variables: {
                    idVisita,
                    fechaInicioUsuario: fechaInicioUsuario,
                    fechaFinUsuario: fechaFinUsuario
                }
            });

            // Si hay error de discrepancia horaria, mostrar modal
            if (!!dataValidarDiscrepanciaHoraria?.validarDiscrepanciaHorariaVisita?.error) {
                setErrorDiscrepanciaHoraria(dataValidarDiscrepanciaHoraria.validarDiscrepanciaHorariaVisita.error);
                setModalMensajeDiscrepanciaHoraVisita(true);

                // Retornar una promesa que se resuelve cuando el usuario interactúa con el modal
                return new Promise((resolve) => {
                    pendingRef.current = { resolve };
                });
            }

            // No hay error, permitir continuar
            return { permitir: true };
        } catch (error) {
            console.error("Error al validar discrepancia horaria:", error);
            throw error;
        }
    };

    /**
     * Handler para cuando el usuario acepta la validación
     */
    const handleAccept = () => {
        setModalMensajeDiscrepanciaHoraVisita(false);
        const pending = pendingRef.current;

        if (pending?.resolve) {
            pendingRef.current = {};
            pending.resolve({ permitir: true });
        }
    };

    /**
     * Handler para cuando el usuario cancela la validación
     */
    const handleCancel = () => {
        setModalMensajeDiscrepanciaHoraVisita(false);
        const pending = pendingRef.current;

        if (pending?.resolve) {
            pendingRef.current = {};
            pending.resolve({ permitir: false });
        }
    };

    /**
     * Componente del modal - renderizar en el JSX del componente
     */
    const modal = (
        <MensajeAdvertenciaDialog
            open={modalMensajeDiscrepanciaHorariaVisita}
            onClose={handleCancel}
            onAccept={handleAccept}
            onCancel={handleCancel}
            message={errorDiscrepanciaHoraria}
            acceptText="Finalizar"
            cancelText="Cancelar"
            mustGoBack={false}
        />
    );

    return {
        validarDiscrepancia,
        modal
    };
}